import { useEffect, useMemo, useRef } from 'react';
import browserHistory from '~/browserHistory';
import { useGetPaymentMethods } from '~/domain/Payments';

/**
 * Fetches the office's payment methods once the route job has loaded, so the
 * confirm/decline flow can surface the payment-missing popup. Skips the fetch
 * when returning to the screen via the browser back button, and de-dupes per
 * office so a given office is only fetched once.
 */
export const usePaymentsForJob = ({
  job,
  jobId,
  hasLoadedJob,
  allCandidates,
  visibleCandidates,
}) => {
  const paymentsFetchOfficeIdRef = useRef(null);
  const officeId = job?.offer_owner?.id;

  const shouldFetchPayments = useMemo(() => {
    if (!hasLoadedJob || jobId !== job?.id || !job?.specialty) {
      return false;
    }

    const backFromCandidateProfile =
      allCandidates?.length && visibleCandidates?.length && browserHistory.action === 'POP';

    if (backFromCandidateProfile) {
      return false;
    }

    if (!officeId || paymentsFetchOfficeIdRef.current === officeId) {
      return false;
    }

    return true;
  }, [
    allCandidates,
    hasLoadedJob,
    job?.id,
    job?.specialty,
    jobId,
    officeId,
    visibleCandidates,
  ]);

  const paymentMethodsQuery = useGetPaymentMethods(officeId, {
    enabled: shouldFetchPayments,
  });

  useEffect(() => {
    if (shouldFetchPayments && officeId) {
      paymentsFetchOfficeIdRef.current = officeId;
    }
  }, [officeId, shouldFetchPayments]);

  return paymentMethodsQuery;
};
